/**
 * TemplateThumbnail.tsx
 *
 * Lazy iframe preview of one email template.
 *
 * Template lists used to render thumbnails from html the list endpoint returned
 * inline. Email bodies are big — a single DnD template can pass 800 KB, and
 * Laravel's JSON escaping roughly doubles it on the wire — so a page of 200 cards
 * asked for a response the gateway refuses to deliver. The request failed whole
 * and the grid rendered nothing.
 *
 * So markup is fetched per card, and only once a card is actually near the
 * viewport: a grid of hundreds costs the same as a grid of nine until the viewer
 * scrolls. React Query caches by template id, so scrolling back is free, and the
 * dedicated /preview endpoint returns one body without the DnD model.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Mail } from 'lucide-react';
import * as marketingSvc from '@/services/marketing.service';
import { cn } from '@/lib/utils';

interface TemplateThumbnailProps {
  templateId: number;
  /** From the list row. When false we render the empty state and never fetch. */
  hasContent: boolean;
  title: string;
  /**
   * Markup the caller already holds — from a withContent list, or a template just
   * saved. Skips the fetch entirely when present.
   */
  html?: string | null;
  /** Width the iframe renders its document at before scaling. */
  renderWidth?: number;
  /** Scale applied to that document to fit the card. */
  scale?: number;
  className?: string;
}

export function TemplateThumbnail({
  templateId,
  hasContent,
  title,
  html,
  renderWidth = 600,
  scale = 0.47,
  className,
}: TemplateThumbnailProps) {
  const holderRef = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);

  // Watch until the card first comes near the viewport, then stop — a thumbnail
  // never needs re-fetching because it scrolled away.
  useEffect(() => {
    if (inView || !hasContent || html) return;
    const node = holderRef.current;
    if (!node) return;

    // Without IntersectionObserver, fetch immediately rather than show nothing.
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      // Start slightly early so the preview is usually there by the time the
      // card is legible.
      { rootMargin: '200px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [inView, hasContent, html]);

  const { data: fetched, isLoading } = useQuery({
    queryKey: ['marketing', 'email-template-preview', templateId],
    queryFn: () => marketingSvc.getEmailTemplatePreview(templateId),
    enabled: inView && hasContent && !html,
    staleTime: 5 * 60 * 1000,
  });

  const markup = html ?? fetched ?? null;

  return (
    <div ref={holderRef} className={cn('w-full h-full overflow-hidden relative', className)}>
      {markup ? (
        <iframe
          srcDoc={markup}
          className="absolute top-0 left-0 border-none pointer-events-none"
          style={{
            width: `${renderWidth}px`,
            height: `${Math.round(renderWidth * 1.33)}px`,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
          sandbox="allow-same-origin"
          title={title}
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          {hasContent && (isLoading || !inView) ? (
            <div className="w-8 h-8 rounded bg-slate-200 animate-pulse" />
          ) : (
            <Mail className="h-8 w-8 text-slate-200" />
          )}
        </div>
      )}
    </div>
  );
}

export default TemplateThumbnail;
